import { I18n } from "@assets/strings";
import React, { useState } from "react";
import { ClipLoader } from "react-spinners";
import styled, { css } from "styled-components";
import { useServices } from "@core/servicesContext";
import { Media } from "@domain/media/media";
import { useAsync } from "@utils/hooks";
import { SecondaryButton } from "@ui/components/buttons/secondaryButton";
import { Primary } from "@ui/components/containers/primary";
import { AssetManagerIcon } from "@ui/components/icons";
import { Stack, stack } from "@ui/components/layout";
import { AssetBrowser } from "@ui/components/media/assetBrowser/assetBrowser";
import { UploadArea } from "@ui/components/media/uploadArea";
import { FileUpload } from "@ui/components/mediaEditor/fileUpload";
import { ClosableModal } from "@ui/modals/closableModal";
import { useModalTools } from "@ui/modals/modalContext";
import { mobile } from "@ui/styles/utils";
import { createToast } from "@ui/toasts/utils";
import { SizeLimitModal } from "./sizeLimitModal";

interface AssetUploadModalProps extends React.HTMLAttributes<HTMLDivElement> {
	close: () => void;
	workspaceId: string;
}
export const AssetUploadModal: React.FC<AssetUploadModalProps> = ({ close, workspaceId, ...htmlProps }) => {
	const { workspaceService } = useServices();
	const { createModal } = useModalTools();
	const [browsingAssets, setBrowsingAssets] = useState(false);
	const { run, loading } = useAsync(async (media: Media) => {
		try {
			await workspaceService.addAsset(workspaceId, media);
			createToast(I18n.t("success"), I18n.t("workspace.assets.create.success"), "success");
			close();
		} catch (e) {
			const error = e as { code?: number };
			if (error.code === 400) {
				close();
				createModal((close) => <SizeLimitModal close={close} />);
			} else {
				createToast(
					I18n.t("error"),
					I18n.t(error.code === 409 ? "workspace.assets.create.exists" : "workspace.assets.create.error"),
					"error"
				);
			}
		}
	}, []);

	return (
		<Container {...htmlProps} close={close} browsingAssets={browsingAssets}>
			{loading ? (
				<>
					<ClipLoader />
				</>
			) : browsingAssets ? (
				<AssetBrowser
					onAssetsChosen={(assets) => {
						const asset = assets.get(0);
						if (asset) {
							run(asset);
						}
						setBrowsingAssets(false);
					}}
					onCancel={() => setBrowsingAssets(false)}
				/>
			) : (
				<>
					<FileUpload
						rename
						type="file"
						onUpload={(media) => {
							run(media);
						}}
					>
						{(dragging, loading, progress) => <UploadArea dragging={dragging} loading={loading} progress={progress} />}
					</FileUpload>
					<Stack gap={6} align="center">
						<Primary>{I18n.t("asset.modal.asset_manager")}</Primary>
						<SecondaryButton lighter onClick={() => setBrowsingAssets(true)}>
							<AssetManagerIcon />
							{I18n.t("asset_manager.mine.title")}
						</SecondaryButton>
					</Stack>
				</>
			)}
		</Container>
	);
};

const Container = styled(ClosableModal).withConfig({
	shouldForwardProp: (prop) => !["browsingAssets"].includes(prop),
})<{ browsingAssets?: boolean }>`
	${stack(20, "flex-start", "stretch")};

	${({ browsingAssets }) =>
		browsingAssets &&
		css`
			padding-bottom: 12px;
			@media ${mobile} {
				padding-bottom: 6px;
			}
		`}

	> button {
		align-self: center;
	}
`;
